import React, { useReducer, useState } from "react"
import axios from "axios"

interface HoleSponsorParams {
  name: string
  subtitle: string
  holeNumber: string
  linkUrl: string
  description: string
}

const initialParams: HoleSponsorParams = {
  name: "",
  subtitle: "",
  holeNumber: "",
  linkUrl: "",
  description: ""
}

interface paramsReducerAction {
  type: string
  payload: string
}

interface HoleSponsorFormProps {
  holeSponsorId: number
  params: HoleSponsorParams
  backPath: string
  subtitleLength: number
  holeSponsorPhotoUrl: string
  holeOptions: [string, string][]
}

const descriptionLength = 1000

const paramsReducer = (state: HoleSponsorParams, action: paramsReducerAction) => {
  switch (action.type) {
    case "name":
      return { ...state, name: action.payload }
    case "subtitle":
      return { ...state, subtitle: action.payload }
    case "holeNumber":
      return { ...state, holeNumber: action.payload }
    case "linkUrl":
      return { ...state, linkUrl: action.payload }
    case "description":
      return { ...state, description: action.payload }
    default:
      return state
  }
}

export default function HoleSponsorForm(props: HoleSponsorFormProps) {
  const { holeSponsorId, backPath, subtitleLength, holeSponsorPhotoUrl, holeOptions } = props
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [params, dispatch] = useReducer(paramsReducer, props.params || initialParams)

  const updateParam = (name: string, value: string) => {
    dispatch({ type: name, payload: value })
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    if (submitting) return

    setSubmitting(true)
    setError(null)

    let photoS3Url: string = null
    if (file) {
      let url: string
      try {
        const response = await axios.get(
          `/app/hole_sponsors/${holeSponsorId}/presigned_s3_url?filename=${encodeURIComponent(file.name)}`
        )
        url = response.data.url
      } catch (e) {
        console.log(e)
        setError("Could not get Presigned S3 URL")
        setSubmitting(false)
        window.scrollTo(0, 0)
        return
      }

      try {
        await axios.put(url, file, { headers: { "Content-Type": file.type } })
      } catch (e) {
        console.log(e)
        setError("Upload failed")
        setSubmitting(false)
        window.scrollTo(0, 0)
        return
      }

      photoS3Url = url.split("?")[0]
    }

    try {
      const requestParams = photoS3Url ? { ...params, photoS3Url } : params

      const response = await axios.patch(`/app/hole_sponsors/${holeSponsorId}`, requestParams)

      if (response.status === 200 && response.data?.redirectUrl) {
        // @ts-ignore
        window.location.href = response.data.redirectUrl
      } else {
        console.log(response)
        setError("Update failed, invalid response")
        window.scrollTo(0, 0)
      }
    } catch (e) {
      console.log(e)
      if (e.response?.status === 422) {
        setError(`Update failed: ${e.response.data.error}`)
        window.scrollTo(0, 0)
      } else {
        setError("Update failed")
        window.scrollTo(0, 0)
      }
    }

    setSubmitting(false)
  }

  return (
    <form encType="multipart/form-data" className="form-horizontal" onSubmit={handleUpload}>
      <input type="hidden" name="backPath" value={backPath} />

      {error && <div id="error">{error}</div>}

      <div className="field">
        <label htmlFor="name">Business Name</label>
        <input type="text" name="name" value={params.name} onChange={(e) => updateParam("name", e.target.value)} />
      </div>

      <div className="field" style={{ marginTop: "1rem" }}>
        <label htmlFor="subtitle">Subtitle</label>
        <p style={{ fontSize: "0.75rem" }}></p>
        <input
          type="text"
          name="subtitle"
          value={params.subtitle}
          onChange={(e) => updateParam("subtitle", e.target.value)}
          maxLength={subtitleLength}
        />
        <p style={{ fontSize: "0.75rem" }}>
          {params.subtitle?.length || 0}/{subtitleLength} characters remaining
        </p>
      </div>

      <div className="field" style={{ marginTop: "1rem" }}>
        <label htmlFor="description">Description</label>
        <textarea
          name="description"
          value={params.description}
          onChange={(e) => updateParam("description", e.target.value)}
          rows={6}
          maxLength={descriptionLength}
        />
        <p style={{ fontSize: "0.75rem" }}>
          {params.description?.length || 0}/{descriptionLength} characters remaining
        </p>
      </div>

      <div className="field" style={{ marginTop: "1rem" }}>
        <label htmlFor="linkUrl">Website URL</label>
        <input
          type="url"
          name="linkUrl"
          value={params.linkUrl}
          onChange={(e) => updateParam("linkUrl", e.target.value)}
        />
      </div>

      <div className="field" style={{ marginTop: "1rem" }}>
        <label htmlFor="photo" style={{ display: "block" }}>
          Photo
        </label>
        {holeSponsorPhotoUrl && <img src={holeSponsorPhotoUrl} alt="Hole Sponsor Photo" />}
        <input
          type="file"
          name="photo"
          id="photo"
          className="form-control"
          onChange={handleFileChange}
          accept="image/*"
        />
        <p style={{ marginTop: "0.5rem" }}>960x480 JPG or PNG images work best</p>
      </div>

      <div className="field" style={{ marginTop: "1rem" }}>
        <label htmlFor="holeNumber">Hole Number</label>
        <select name="holeNumber" value={params.holeNumber} onChange={(e) => updateParam("holeNumber", e.target.value)}>
          <option value="">Select a Hole</option>
          {holeOptions.map(([label, value]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="screen-actions">
        <button type="submit" className="action-button block" disabled={submitting}>
          Save
        </button>
      </div>
    </form>
  )
}
